"use client";

import React from "react";
import { TrendingUp, Car, Headset, Loader, CircleCheck, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function PartnershipForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-green-500/20 flex items-center justify-center">
          <CircleCheck className="size-10 text-green-500" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">Request Received</h3>
          <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="company_name" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp." {...field} />
              </FormControl>
              <FormDescription>
                Enter the legal name of your organization.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_name" rules={{
        required: "Contact person is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} />
              </FormControl>
              <FormDescription>
                Full name of the primary contact.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane.doe@acme.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-14567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="vehicle_quantity" rules={{
        min: {
          value: 1,
          message: "Must be at least 1"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Estimated Number of Vehicles Needed</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 10" {...field} />
              </FormControl>
              <FormDescription>
                Provide an approximate quantity to help us tailor a solution.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please provide details about your needs"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Details / Requirements</FormLabel>
            <FormControl>
              <Textarea className="min-h-[100px]" placeholder="Tell us about your partnership needs..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-semibold shadow-lg shadow-primary/20">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Request...
          </> : "Submit Partnership Inquiry"}
      </Button>
    </Form>;
}
export default function EnterprisePartnershipHub() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const features = [{
    icon: TrendingUp,
    title: "Tax Efficiency",
    description: "Optimize your corporate expenses with our tailored leasing structures designed for maximum tax benefits and financial transparency."
  }, {
    icon: Car,
    title: "Fleet Management",
    description: "Access a modern, diverse fleet of well-maintained vehicles. We handle the maintenance, insurance, and logistics so you can focus on business."
  }, {
    icon: Headset,
    title: "24/7 Priority Support",
    description: "Dedicated account managers and round-the-clock roadside assistance ensure your operations never face downtime."
  }];
  return <section id="enterprise-partnership-hub" ref={sectionRef} className={`dark bg-background text-foreground py-20 px-4 md:px-6 overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
          
          <div className={`lg:col-span-5 flex flex-col transition-all duration-700 delay-200 ${isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}`}>
            <Card className="h-full bg-card border-border/50 shadow-2xl shadow-black/20 overflow-hidden relative group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none" />
              
              <CardHeader className="pb-8 relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider w-fit mb-4">
                  <Building2 className="size-3" />
                  Corporate Solutions
                </div>
                <CardTitle className="text-3xl md:text-4xl font-bold tracking-tight text-white">
                  Why Partner With <span className="text-primary">Ledotravel</span>?
                </CardTitle>
                <CardDescription className="text-lg text-muted-foreground mt-4">
                  Elevate your business mobility with a partner that understands the demands of modern enterprise.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-8 relative z-10">
                {features.map((feature, index) => <div key={index} data-index={index} className="flex gap-5 group/item">
                    <div className="shrink-0">
                      <div className="size-12 rounded-xl bg-background/50 border border-white/10 flex items-center justify-center shadow-lg shadow-primary/5 group-hover/item:shadow-primary/20 group-hover/item:border-primary/30 transition-all duration-300">
                        <feature.icon className="size-6 text-primary drop-shadow-[0_0_8px_rgba(59,130,246,0.6)]" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-lg font-semibold text-white group-hover/item:text-primary transition-colors">
                        {feature.title}
                      </h4>
                      <p className="text-muted-foreground leading-relaxed text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>)}
              </CardContent>
            </Card>
          </div>

          <div className={`lg:col-span-7 transition-all duration-700 delay-400 ${isVisible ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"}`}>
            <div className="h-full bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 md:p-8 lg:p-10 shadow-xl">
              <div className="mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  Contact us for more information
                </h2>
                <p className="text-muted-foreground">
                  Fill out the form below to discuss your fleet requirements. Our corporate team will design a custom proposal for your organization.
                </p>
              </div>

              <FormProvider formId="enterprise_partnership_form_home" sectionName="enterprise-partnership-hub" defaultValues={{
              company_name: "",
              contact_name: "",
              email: "",
              phone: "",
              vehicle_quantity: "",
              message: ""
            }} validationRules={{
              company_name: {
                required: true
              },
              contact_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              message: {
                required: true
              }
            }} submitText="Submit Partnership Inquiry" successMessage="Thank you for your interest! Our corporate partnership team will review your requirements and contact you within 24 hours." errorMessage="We encountered an issue submitting your request. Please try again or contact support directly.">
                <PartnershipForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}